import { useFetchAvailableClubMembersQuery } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/Avatar";
import { userDisplayName } from "@tinyclub/shared/utils";

interface AvailableIRLSectionProps {
  clubId: string;
  onMembersClick?: () => void;
}

/**
 * AvailableIRLSection - Shows members who are available IRL.
 *
 * Layout per Figma design:
 * - Row 1: "Available IRL" label (text-primary) with blue signal-stream icon
 * - Row 2: First 10 member avatars (32px, overlapping -8px margin)
 * - Entire section clickable -> opens MembersListModal with availableOnly filter
 */
export function AvailableIRLSection({
  clubId,
  onMembersClick,
}: AvailableIRLSectionProps) {
  const { data: availableMembers = [] } = useFetchAvailableClubMembersQuery({
    clubId,
  });

  // Don't render if no members are available IRL
  if (availableMembers.length === 0) {
    return null;
  }

  // Show first 10 members
  const displayMembers = availableMembers.slice(0, 10);
  const remainingCount = availableMembers.length - displayMembers.length;

  return (
    <button
      onClick={onMembersClick}
      className="flex flex-col gap-2 rounded-lg hover:bg-surface-1 transition-colors cursor-pointer text-left"
    >
      {/* Row 1: Label with icon - text comes first, then blue signal-stream icon */}
      <div className="flex items-center gap-2">
        <span className="text-body-2-medium text-text-primary">Available IRL</span>
        <Icon name="signal-stream" size={14} className="text-[#1f4ab3]" />
      </div>

      {/* Row 2: Avatars (overlapping) with blue IRL dots */}
      <div className="flex items-center pr-2">
        {displayMembers.map((member, index) => {
          const profile = member.user_profile;
          const displayName = userDisplayName(profile);
          const avatarUrl = profile.avatar_small || profile.avatar_medium;

          return (
            <div
              key={member.user_id}
              className="relative"
              style={{ marginLeft: index === 0 ? 0 : -8 }}
            >
              <Avatar className="w-8 h-8 border border-border-tertiary">
                {avatarUrl ? (
                  <AvatarImage src={avatarUrl} alt={displayName} />
                ) : null}
                <AvatarFallback className="text-xs bg-surface-1">
                  {displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {/* Blue IRL dot adornment */}
              <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#1f4ab3] border-2 border-background" />
            </div>
          );
        })}

        {/* +N indicator if there are more members */}
        {remainingCount > 0 && (
          <div
            className="relative flex items-center justify-center w-8 h-8 rounded-full bg-surface-2 border-2 border-background text-body-3-medium text-text-secondary"
            style={{ marginLeft: -8 }}
          >
            +{remainingCount}
          </div>
        )}
      </div>
    </button>
  );
}
